import { SchemaDescription } from 'yup';
import { RendererType } from '../data-models/field-definition';
import { PrimitiveType } from '../data-models/field3.data-model';
import { DateFormattersId } from '@foresightdata/web-common-core';

/** The meta keys that say what a schema field holds, beyond its yup type. */
interface DataTypeMeta {
  dataType?: PrimitiveType;
  renderer?: RendererType;
  format?: unknown;
  formatter?: unknown;
}

/** A date by the grid's rule: a yup date, or a date formatter declared in meta. */
export function isDateSchemaField(fieldDescription: SchemaDescription): boolean {
  const meta = fieldDescription.meta as DataTypeMeta | undefined;
  return (
    fieldDescription.type === 'date' ||
    (meta?.format as string) in DateFormattersId ||
    (meta?.formatter as string) in DateFormattersId
  );
}

/**
 * The primitive a schema field holds. `meta.dataType` declares it outright — a string that
 * carries an ISO date says `dataType: 'date'` (see isoDateStringField) — otherwise a date is
 * anything rendered as one (a yup date, a date formatter, a date renderer), else the yup type.
 */
export function schemaFieldDataType(fieldDescription: SchemaDescription): PrimitiveType {
  const meta = fieldDescription.meta as DataTypeMeta | undefined;
  if (meta?.dataType) {
    return meta.dataType;
  }
  if (isDateSchemaField(fieldDescription) || meta?.renderer === RendererType.date) {
    return 'date';
  }
  return fieldDescription.type as PrimitiveType;
}

/** True for a meta declaring `dataType: 'date'`, the one case the grid's own date rule does not cover. */
export function declaresDate(fieldDescription: SchemaDescription): boolean {
  return (fieldDescription.meta as DataTypeMeta | undefined)?.dataType === 'date';
}
